import { useForm, FormProvider, Controller } from "react-hook-form";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import PaperDraggable from "../../../../../../../../components/PaperDraggable";
import { formatarCPFCNPJ, formatarTelefone } from "../../../../../../../../helpers/masks";
import { grausParentesco, sexos, tiposRelacionamento } from "./helpers";

interface FormData {
  nome: string;
  documento: string;
  sexo: string;
  grau_parentesco: string;
  tipo_relacionamento: string;
  telefone: string;
}

interface DialogIncluirRelacionamentoProps {
  open: boolean;
  onClose: () => void;
  cooperadoId: string;
}

export default function DialogIncluirRelacionamento({
  open,
  onClose,
  cooperadoId
}: DialogIncluirRelacionamentoProps) {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(() => ({
    nome: "",
    documento: "",
    sexo: "",
    grau_parentesco: "",
    tipo_relacionamento: "",
    telefone: "",
  }), []);

  const methods = useForm<FormData>({
    defaultValues,
    values: defaultValues
  });

  const { control, handleSubmit } = methods;

  const codigoPublico = cooperadoId.slice(1);
  const tipoPublico = cooperadoId[0]

  const { mutate: incluirRelacionamento } = useMutation({
    mutationKey: ["incluir-relacionamento", cooperadoId],
    mutationFn: (formData: FormData) =>
      axiosClient.post(`cadastros/incluir-relacionamento`, {
        ...formData,
        codigo_publico: codigoPublico,
        tipo_publico: tipoPublico
      }),
    onError: () => {
      enqueueSnackbar("Erro ao incluir relacionamento!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries('cadastros/detalhar-cooperado')
      enqueueSnackbar("Relacionamento incluído com sucesso!", {
        variant: "success",
      });
      onClose();
    },
  });

  const handleFormSubmit = (formData: FormData) => {
    if (!formData.nome.trim()) {
      enqueueSnackbar("Nome é obrigatório.", { variant: "warning" });
      return;
    }

    incluirRelacionamento(formData);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      PaperComponent={PaperDraggable}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
        Incluir Relacionamento
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id="incluir-relacionamento-form" onSubmit={handleSubmit(handleFormSubmit)}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="documento"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Documento"
                      fullWidth
                      onChange={(e) => {
                        const value = formatarCPFCNPJ(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="telefone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Telefone"
                      fullWidth
                      onChange={(e) => {
                        const value = formatarTelefone(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="sexo"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Sexo</InputLabel>
                      <Select
                        {...field}
                        label="Sexo"
                        displayEmpty
                        value={field.value || ""}
                      >
                        {sexos.map((sexo) => (
                          <MenuItem key={sexo.value} value={sexo.value}>
                            {sexo.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="tipo_relacionamento"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Tipo Relacionamento</InputLabel>
                      <Select
                        {...field}
                        label="Tipo Relacionamento"
                      >
                        {tiposRelacionamento.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>
                            {tipo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="grau_parentesco"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Grau Parentesco</InputLabel>
                      <Select
                        {...field}
                        label="Grau Parentesco"
                      >
                        {grausParentesco.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>
                            {tipo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          type="submit"
          form="incluir-relacionamento-form"
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}